/**
 * Enhanced logging utility with different levels and production safety
 */

// Log levels for filtering output
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
};

class Logger {
  constructor() {
    // Set log level based on environment (less verbose in production)
    this.level = process.env.NODE_ENV === 'production' ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;
    this.prefix = '[ConstellationSociale]';
  }

  _shouldLog(level) {
    return level <= this.level;
  }

  _formatMessage(level, message, ...args) {
    const timestamp = new Date().toISOString();
    const levelName = Object.keys(LOG_LEVELS)[level];
    return [`${this.prefix} [${timestamp}] [${levelName}]`, message, ...args];
  }

  error(message, ...args) {
    if (this._shouldLog(LOG_LEVELS.ERROR)) {
      console.error(...this._formatMessage(LOG_LEVELS.ERROR, message, ...args));
    }
  }

  warn(message, ...args) {
    if (this._shouldLog(LOG_LEVELS.WARN)) {
      console.warn(...this._formatMessage(LOG_LEVELS.WARN, message, ...args));
    }
  }

  info(message, ...args) {
    if (this._shouldLog(LOG_LEVELS.INFO)) {
      console.info(...this._formatMessage(LOG_LEVELS.INFO, message, ...args));
    }
  }

  debug(message, ...args) {
    if (this._shouldLog(LOG_LEVELS.DEBUG)) {
      console.log(...this._formatMessage(LOG_LEVELS.DEBUG, message, ...args));
    }
  }

  // API specific logging
  apiCall(endpoint, method = 'GET', status = null) {
    if (status) {
      this.debug(`API ${method} ${endpoint} - Status: ${status}`);
    } else {
      this.debug(`API ${method} ${endpoint}`);
    }
  }

  apiError(endpoint, method, error) {
    this.error(`API ${method} ${endpoint} failed:`, error.message);
  }

  // Performance logging
  performance(operation, duration) {
    this.debug(`Performance: ${operation} took ${duration}ms`);
  }

  // State management logging
  stateChange(entity, action, data = null) {
    this.debug(`State: ${entity} ${action}`, data ? { id: data.id, name: data.nom || data.name } : '');
  }

  // Cache logging
  cacheHit(key) {
    this.debug(`Cache hit: ${key}`);
  }

  cacheMiss(key) {
    this.debug(`Cache miss: ${key}`);
  }

  cacheInvalidate(key) {
    this.debug(`Cache invalidated: ${key}`);
  }
}

// Create singleton instance
export const logger = new Logger();

// Export for testing
export { Logger };
